import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./HustleEatsPage.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
/* HustleEats diner wallet. Shows venue-locked + Hustle-wide balances
   plus a per-credit history with the 90-day expiry timer and the
   bad-experience review escape hatch surfaced in plain language. */

function moneyFromCents(c) {
  return ((c || 0) / 100).toFixed(2);
}

function daysUntil(iso) {
  const ms = new Date(iso) - new Date();
  return Math.max(0, Math.ceil(ms / 86400000));
}

export default function HustleEatsWalletPage() {
  const [wallet, setWallet] = useState(null);
  const [authErr, setAuthErr] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWallet = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/hustleeats/wallet/me`, {
          credentials: "include",
        });
        if (res.status === 401) {
          setAuthErr(true);
          return;
        }
        const data = await res.json();
        setWallet(data);
      } finally {
        setLoading(false);
      }
    };
    fetchWallet();
  }, []);

  if (loading) {
    return (
      <div className="he-page">
        <div className="he-state">Loading your wallet…</div>
      </div>
    );
  }

  if (authErr) {
    return (
      <div className="he-page">
        <div className="he-hero">
          <div className="he-hero__inner">
            <span className="he-eyebrow">HustleEats · Wallet</span>
            <h1 className="he-h1">Sign in to see your credit.</h1>
            <p className="he-lede">
              Every time you fill an empty seat, restaurants drop a thank-you credit in your
              wallet. Sign in or create your free account to see your balance.
            </p>
            <Link to="/admin/login" className="he-hero__link" data-testid="he-wallet-signin">Sign in →</Link>
          </div>
        </div>
      </div>
    );
  }

  const venue = wallet?.venue_locked_cents || 0;
  const wide = wallet?.hustle_wide_cents || 0;
  const total = wallet?.total_cents || 0;

  return (
    <div className="he-page">
      <header className="he-hero">
        <div className="he-hero__inner">
          <span className="he-eyebrow">HustleEats · Wallet</span>
          <h1 className="he-h1">${moneyFromCents(total)} in thank-you credit.</h1>
          <p className="he-lede">
            Restaurants thanked you for filling empty seats. Use it next visit.
          </p>
          <div className="he-hero__actions">
            <Link to="/eat" className="he-hero__link" data-testid="he-wallet-back">← Browse offers</Link>
          </div>
        </div>
      </header>

      <section className="he-grid" style={{ gridTemplateColumns: "1fr 1fr", maxWidth: 760 }}>
        <article className="he-card" data-testid="he-wallet-venue">
          <div className="he-card__body">
            <div className="he-card__chips">
              <span className="he-chip he-chip--deal">Venue-locked</span>
            </div>
            <h3 className="he-card__title" style={{ fontSize: 32 }}>${moneyFromCents(venue)}</h3>
            <p className="he-card__blurb">
              Use this at the restaurant that gave it to you. Auto-converts to Hustle-wide
              credit after 90 days, or instantly if you rate the venue 2★ or below in the
              post-visit survey.
            </p>
          </div>
        </article>
        <article className="he-card" data-testid="he-wallet-wide">
          <div className="he-card__body">
            <div className="he-card__chips">
              <span className="he-chip he-chip--deal" style={{ background: "var(--he-rust)", color: "var(--he-cream)" }}>Hustle-wide</span>
            </div>
            <h3 className="he-card__title" style={{ fontSize: 32 }}>${moneyFromCents(wide)}</h3>
            <p className="he-card__blurb">
              Spend at any HustleEats venue. No expiry within 12 months of being unlocked.
            </p>
          </div>
        </article>
      </section>

      <section className="he-grid" style={{ paddingTop: 0 }}>
        {(wallet?.credits || []).length === 0 && (
          <div className="he-state" data-testid="he-wallet-empty">
            No credit yet. Fill an empty seat and a thank-you will drop in your wallet.
          </div>
        )}
        {(wallet?.credits || []).map((c) => (
          <article className="he-card" key={c.id} data-testid={`he-wallet-credit-${c.id}`}>
            <div className="he-card__body">
              <div className="he-card__chips">
                <span className={`he-chip he-chip--${c.scope === "venue_locked" ? "deal" : "soldout"}`}>
                  {c.scope === "venue_locked" ? c.venue_slug : "Hustle-wide"}
                </span>
              </div>
              <h3 className="he-card__title">
                ${moneyFromCents(c.remaining_cents)}
                {c.remaining_cents !== c.amount_cents && (
                  <span style={{ fontSize: 14, color: "rgba(58,47,36,0.5)" }}> / ${moneyFromCents(c.amount_cents)}</span>
                )}
              </h3>
              <p className="he-card__blurb">
                Earned {new Date(c.issued_at).toLocaleDateString("en-AU")} · expires in {daysUntil(c.expires_at)} days
              </p>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
